import { useState, useRef, useCallback } from 'react'
import type { ImperativePanelHandle } from 'react-resizable-panels'
import type { ViewMode } from '@/components/layout/document-viewer.types'
import { usePersistedState } from '@/hooks/use-persisted-state'
import { StorageKeys, StorageSchemas } from '@/lib/storage'

/**
 * Return type for use-view-mode hook
 */
export interface UseViewModeReturn {
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  showInfoPane: boolean
  setShowInfoPane: (show: boolean) => void
  infoPanelRef: React.RefObject<ImperativePanelHandle | null>
  documentPanelSizes: number[]
  setDocumentPanelSizes: (sizes: number[]) => void
  handleCloseInfoPane: () => void
}

/**
 * Hook for managing view mode state (single/double/diff/list) and info pane.
 *
 * Info dock: mount at target % (no 0->N double resize). Content enter motion
 * lives on SecondaryPanel via `@/lib/shell-panel-motion`.
 */
export function useViewMode(): UseViewModeReturn {
  const [showInfoPane, setShowInfoPane] = useState(false)
  const infoPanelRef = useRef<ImperativePanelHandle | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>("single")
  const [documentPanelSizes, setDocumentPanelSizes] = usePersistedState<number[]>({
    key: StorageKeys.DOCUMENTS_PANEL_SIZES,
    defaultValue: [50, 50],
    schema: StorageSchemas.documentPanelSizes,
  })

  const handleCloseInfoPane = useCallback(() => {
    setShowInfoPane(false)
  }, [])

  return {
    viewMode,
    setViewMode,
    showInfoPane,
    setShowInfoPane,
    infoPanelRef,
    documentPanelSizes,
    setDocumentPanelSizes,
    handleCloseInfoPane,
  }
}
